import React, { useEffect, useState } from 'react';
import { Row, Col, Alert, Spinner } from 'react-bootstrap';
import Container from '../components/Container';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, RefreshCw, FileText, Download } from 'lucide-react';
import '../styles/components.css';

const AdminCandidatures = () => {
  const { isAuthenticated } = useAuth();
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiUrl =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');

  const fetchCandidatures = async () => {
    if (!apiUrl) {
      setError(
        "REACT_APP_API_URL n'est pas configuré. Ajoutez-le dans le fichier .env du frontend."
      );
      setLoading(false);
      return;
    }

    try {
      setError('');
      const response = await fetch(
        `${apiUrl.replace(/\/$/, '')}/api/candidatures`,
        {
          headers: {
            Accept: 'application/json'
          }
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Erreur ${response.status}`);
      }
      setCandidatures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement candidatures:', err);
      setError("Impossible de charger les candidatures. Vérifiez le backend.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchCandidatures();

    // Rafraîchissement toutes les 15 secondes pour un "temps réel" léger
    const interval = setInterval(fetchCandidatures, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="section">
      <Container>
        <Row className="mb-4">
          <Col>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 'var(--spacing-md)'
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: 'var(--font-size-4xl)',
                    fontWeight: 'var(--font-weight-bold)',
                    marginBottom: 'var(--spacing-sm)',
                    color: 'var(--color-text)'
                  }}
                >
                  Candidatures reçues
                </h1>
                <p
                  style={{
                    color: 'var(--color-text-light)',
                    margin: 0
                  }}
                >
                  Liste des candidatures envoyées via le formulaire « Nous rejoindre ».
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <Button
                  variant="outline"
                  onClick={fetchCandidatures}
                  disabled={loading}
                >
                  <RefreshCw
                    size={18}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Rafraîchir
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft
                    size={18}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Retour
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <div className="card-modern">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--spacing-lg)',
              gap: 'var(--spacing-md)',
              flexWrap: 'wrap'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <FileText size={22} style={{ color: 'var(--color-primary)' }} />
              <h2
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  margin: 0,
                  color: 'var(--color-text)'
                }}
              >
                Tableau des candidatures
              </h2>
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)'
              }}
            >
              Total : {candidatures.length}
            </span>
          </div>

          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {loading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--spacing-xl)'
              }}
            >
              <Spinner animation="border" role="status" size="sm" className="me-2" />
              <span style={{ color: 'var(--color-text-light)' }}>Chargement des candidatures...</span>
            </div>
          ) : candidatures.length === 0 ? (
            <Alert variant="info" style={{ textAlign: 'center' }}>
              Aucune candidature reçue pour le moment.
            </Alert>
          ) : (
            <div
              style={{
                overflowX: 'auto'
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)'
                }}
              >
                <thead>
                  <tr>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>ID</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>Nom</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>Message</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>CV</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatures.map((c) => (
                    <tr key={c.id}>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        {c.id}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        {c.nom}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        <a
                          href={`mailto:${c.email}`}
                          style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
                        >
                          {c.email}
                        </a>
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          borderBottom: '1px solid var(--color-border)',
                          maxWidth: 320,
                          whiteSpace: 'normal'
                        }}
                      >
                        {c.message}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        {c.cv_filename ? (
                          <a
                            href={`${apiUrl.replace(/\/$/, '')}/api/candidatures/cv/${encodeURIComponent(c.cv_filename)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              color: 'var(--color-primary)',
                              textDecoration: 'none',
                              fontWeight: 'var(--font-weight-medium)'
                            }}
                          >
                            <Download size={16} />
                            Télécharger le CV
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        {c.created_at}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default AdminCandidatures;

